"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ClientContextController = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const axios_1 = __importDefault(require("axios"));
const react_query_1 = require("@tanstack/react-query");
const AuthStorage_1 = require("../auth/authStorage/AuthStorage");
const ClientContext_1 = require("../clientContext/ClientContext");
const useErrorHandler_1 = require("../../../hooks/useErrorHandler/useErrorHandler");
const requestInterceptors_1 = require("./interceptors/requestInterceptors");
const responseInterceptors_1 = require("./interceptors/responseInterceptors");
const refreshInterceptor_1 = require("./interceptors/refreshInterceptor");
const ClientContextController = ({ children, storageType = 'sessionStorage' }) => {
    const { handleError } = (0, useErrorHandler_1.useErrorHandler)();
    const [isSessionExpired, setIsSessionExpired] = (0, react_1.useState)(false);
    const [hasPermissionChanged, setHasPermissionChanged] = (0, react_1.useState)(false);
    const authStorage = new AuthStorage_1.AuthStorage(storageType);
    const axiosRef = (0, react_1.useRef)(null);
    const axios = (0, react_1.useMemo)(() => {
        const axios = axios_1.default.create({
            headers: {
                'Content-Type': 'application/json',
            },
            baseURL: `${process.env.REACT_APP_API_URL}`,
            withCredentials: true,
        });
        const handleRefreshLogic = () => {
            return new Promise((resolve, reject) => {
                axios
                    .get('/auth/refresh')
                    .then((response) => {
                    const tokens = response.data;
                    // Update stored tokens
                    authStorage.accessToken = tokens?.accessToken?.token;
                    // Calculate absolute expiry timestamp: current time + relative expiry time
                    authStorage.expires = tokens?.accessToken?.expiresIn ? Date.now() + tokens.accessToken.expiresIn : null;
                    // Update axios default authorization header with new token
                    if (tokens?.accessToken?.token) {
                        axios.defaults.headers.common['Authorization'] = `Bearer ${tokens.accessToken.token}`;
                    }
                    resolve(tokens);
                })
                    .catch((error) => {
                    // Clear tokens and authorization header on refresh failure
                    authStorage.accessToken = null;
                    authStorage.expires = null;
                    axios.defaults.headers.common['Authorization'] = undefined;
                    reject(error);
                });
            });
        };
        // Store axios reference for session invalidation
        axiosRef.current = axios;
        axios.interceptors.request.use((config) => (0, requestInterceptors_1.requestSuccessInterceptor)(config, authStorage));
        axios.interceptors.response.use((response) => (0, responseInterceptors_1.responseSuccessInterceptor)(response), 
        //TODO Ustalić jak finalnie ma działać refresh token
        (0, refreshInterceptor_1.refreshInterceptor)(axios, handleRefreshLogic, () => setIsSessionExpired(true)));
        return axios;
    }, []);
    const queryFn = (0, react_1.useCallback)(async ({ queryKey: [url] }) => {
        if (typeof url === 'string') {
            const lowerCaseUrl = url.toLowerCase();
            return await axios.get(lowerCaseUrl);
        }
        throw new Error('Invalid QueryKey');
    }, [axios]);
    const queryClient = (0, react_1.useMemo)(() => {
        return new react_query_1.QueryClient({
            defaultOptions: {
                queries: {
                    queryFn,
                    retry: false,
                    refetchOnWindowFocus: false,
                    keepPreviousData: true,
                },
            },
            queryCache: new react_query_1.QueryCache({
                onError: (error, query) => {
                    const queryOptions = query.options;
                    if (queryOptions && queryOptions?.handleErrorManually) {
                        return;
                    }
                    return handleError({
                        error,
                        omitAPIErrorMessage: queryOptions.omitAPIErrorMessage,
                        omitErrorCodes: queryOptions.omitErrorCodes,
                    });
                },
            }),
        });
    }, [queryFn]);
    return ((0, jsx_runtime_1.jsx)(ClientContext_1.ClientContext.Provider, { value: {
            axios,
            isSessionExpired,
            setIsSessionExpired,
            authStorage,
            hasPermissionChanged,
            setHasPermissionChanged,
        }, children: (0, jsx_runtime_1.jsx)(react_query_1.QueryClientProvider, { client: queryClient, children: children }) }));
};
exports.ClientContextController = ClientContextController;
